import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme import to avoid lint warnings
const PAGE_ID = 'm6_u2_x3_grundfoss_alpha';

const M6_U2_X3_GrundfossAlpha: React.FC = () => {
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: '', body: 'Die Grundfos ALPHA-Serie (z.B. ALPHA2 oder ALPHA3) repräsentiert moderne Hocheffizienz-Umwälzpumpen für Heizungsanlagen in Ein- und Zweifamilienhäusern. Sie sind bekannt für ihre Energieeffizienz, die AUTOADAPT-Funktion und innovative Features für den hydraulischen Abgleich.' },
    { key: 'merkmale', heading: 'Wesentliche Merkmale', body: '• Hohe Energieeffizienz: EC-Motortechnologie, niedriger Energieeffizienzindex (EEI).\n• AUTOADAPT-Funktion: Automatische Anpassung der Pumpenleistung an den tatsächlichen Bedarf des Heizsystems – oft die effizienteste Einstellung.\n• Weitere Regelungsarten: Proportionaldruck, Konstantdruck, konstante Kennlinien/feste Drehzahlen.\n• Benutzerfreundliche Bedienung: Ein-Knopf-Bedienung und LED-Anzeige (aktuelle Leistungsaufnahme sichtbar).\n• Trockenlaufschutz und Deblockierfunktion.' },
    { key: 'abgleich', heading: 'Hydraulischer Abgleich & Kommunikation', body: '• Hydraulischer Abgleich (ALPHA Reader / ALPHA3 mit Grundfos GO Balance App): Neuere Modelle ermöglichen einen vereinfachten hydraulischen Abgleich des Heizsystems für zusätzliche Energieeinsparungen und Komfort.\n• Externe Signale (ALPHA3 / spezifische Modelle): Einige Modelle bieten Eingänge für PWM-Signale oder erweiterte Kommunikationsmöglichkeiten über Grundfos GO Apps (z.B. für Einstellungen, Statusabfragen).' },
    { key: 'anschluss', heading: 'Anschluss der ALPHA Pumpe', body: 'Elektrisch:\n• 1~230V Netzspannung.\n• Meist mit vormontiertem ALPHA-Stecker für einfachen Anschluss.\n\nHydraulisch:\n• Standard-Gewindeanschlüsse (z.B. G 1, G 1 ½, G 2).\n• Einbaulage: Motorwelle muss horizontal sein.' },
    { key: 'wichtig', heading: 'Worauf beim Einsatz achten?', body: '• Für kleinere Einfamilienhäuser ist AUTOADAPT oft die beste Wahl.\n• Bei Bedarf hydraulischen Abgleich mit ALPHA Reader oder GO Balance App durchführen.\n• Für externe Steuerung gezielt ALPHA3-Varianten mit entsprechenden Eingängen wählen.\n• LED-Anzeige für Leistungsaufnahme nutzen, um Energieverbrauch im Blick zu behalten.' }
  ]), []);

  const images = [
    { key: 'img_alpha_front', src: '/assets/drawable/pumpe_alpha3.png', label: 'Grundfos ALPHA Pumpe Frontansicht' },
    { key: 'img_alpha_autoadapt', src: '/assets/drawable/pumpe_alpha2.png', label: 'AUTOADAPT Funktion und ALPHA Reader für hydraulischen Abgleich' },
    { key: 'img_alpha_installation', src: '/assets/drawable/pumpe_alpha_app.png', label: 'Grundfos ALPHA Installationsbeispiel' }
  ];

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override, textBlocks]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Gespeichert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.2.3 Grundfos ALPHA',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    )
  ));
};

export default M6_U2_X3_GrundfossAlpha;


